import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, XCircle, X, Eye, TrendingUp, TrendingDown } from 'lucide-react';
import type { MerchandiseMovement } from '@/types/merchandise-movement.types';
import type { User as AuthUser } from '@/context/AuthContext';
import { 
  getMovementTypeLabel, 
  getMovementTypeColor, 
  getStatusLabel, 
  getStatusColor,
  getDirectionLabel,
  getDirectionColor
} from '@/types/merchandise-movement.types';

interface MerchandiseMovementsTableProps {
  merchandiseMovements: MerchandiseMovement[];
  loading: boolean;
  onApproveClick: (movement: MerchandiseMovement) => void;
  onRejectClick: (movement: MerchandiseMovement) => void;
  onCancelClick: (movement: MerchandiseMovement) => void;
  user?: AuthUser | null;
}

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) {
    return new Date().toLocaleDateString('es-PE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return new Date().toLocaleDateString('es-PE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  return date.toLocaleDateString('es-PE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const MerchandiseMovementsTable: React.FC<MerchandiseMovementsTableProps> = ({
  merchandiseMovements,
  loading,
  onApproveClick,
  onRejectClick,
  onCancelClick,
  user
}) => {
  // Check if user is CAJERO (positionId: 3)
  const isCajero = user?.positionId === 3;
  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="h-16 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  if (merchandiseMovements.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-between mb-4">
          <Eye className="w-12 h-12 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          No se encontraron movimientos de mercadería
        </h3>
        <p className="text-gray-500 max-w-sm mx-auto">
          No hay movimientos de mercadería registrados que coincidan con los filtros aplicados.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {merchandiseMovements.map((movement) => (
        <div key={movement.id} className={`px-4 py-2 border rounded-lg ${
          movement.direction === 'entry'
            ? 'bg-green-50/60 border-green-200 hover:bg-green-50'
            : 'bg-red-50/60 border-red-200 hover:bg-red-50'
        }`}>
          {/* Fila 1: Código + Dirección + Cantidad badge + Insumo + Tipo + Estado + Fecha */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-lg">{movement.movementNumber}</span>
              {movement.direction === 'entry' ? (
                <TrendingUp className="w-4 h-4 text-green-600" />
              ) : (
                <TrendingDown className="w-4 h-4 text-red-600" />
              )}
              <Badge className={getDirectionColor(movement.direction)}>
                {getDirectionLabel(movement.direction)}
              </Badge>
              <Badge className="bg-slate-600 hover:bg-slate-600 text-white text-xs">
                {movement.quantity.toLocaleString('es-PE')} {movement.unit || (movement as any).ingredientUnit || 'UND'}
              </Badge>
              <span className="font-medium text-gray-900">{movement.ingredientName}</span>
              <Badge className="bg-blue-100 text-blue-800 text-xs">
                {movement.movementTypeName || movement.movementType}
              </Badge>
              <Badge className={getStatusColor(movement.status)}>
                {getStatusLabel(movement.status)}
              </Badge>
            </div>
            <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
              {formatDate(movement.requestedAt)}
            </span>
          </div>

          {/* Fila 2: Info + Botones */}
          <div className="flex items-center justify-between mt-1">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
              <span><span className="font-bold">Local:</span> {movement.locationName}</span>
              <span><span className="font-bold">Solicitado por:</span> {movement.requestedBy}</span>
              {movement.reason && (
                <span><span className="font-bold">Razón:</span> {movement.reason}</span>
              )}
            </div>

            <div className="flex items-center gap-2">
              {movement.status === 'pending_approval' && (
                <>
                  {!isCajero && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onApproveClick(movement)}
                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Aprobar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onRejectClick(movement)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <XCircle className="h-4 w-4 mr-1" />
                        Rechazar
                      </Button>
                    </>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onCancelClick(movement)}
                    className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Cancelar
                  </Button>
                </>
              )}
              {movement.status === 'approved' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onCancelClick(movement)}
                  className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                >
                  <X className="h-4 w-4 mr-1" />
                  Cancelar
                </Button>
              )}
              {(movement.status === 'rejected' || movement.status === 'cancelled') && (
                <div className="text-xs text-gray-500 text-right">
                  {movement.status === 'rejected' && movement.rejectedBy && (
                    <div>
                      <span>Rechazado por: {movement.rejectedBy}</span>
                      {movement.rejectionReason && (
                        <span className="ml-2 text-gray-600">- {movement.rejectionReason}</span>
                      )}
                    </div>
                  )}
                  {movement.status === 'cancelled' && movement.cancelledBy && (
                    <div>
                      <span>Cancelado por: {movement.cancelledBy}</span>
                      {movement.cancellationReason && (
                        <span className="ml-2 text-gray-600">- {movement.cancellationReason}</span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};